// src/services/intentionPeriodsService.ts
import { supabase } from '../supabase/config';
import {
  IntentionPeriod,
  IntentionPeriodFormData,
  IntentionPeriodStatus,
  IntentionPeriodStats
} from '../types/intentionPeriods';
import { computeIntentionResponseStats } from '../utils/intentionStats';

/**
 * Service for managing teaching intention submission periods
 */
/**
 * Turn a uniqueness violation on intention_periods into something a human can
 * act on. Two partial/unique indexes guard this table
 * (20260911143000_intention_periods_uniqueness.sql):
 *
 *   uq_intention_periods_tenant_year_name  — one period per name per year
 *   uq_intention_periods_one_open_per_year — at most one OPEN period per year
 *
 * Without this the admin sees a raw Postgres 23505 and no idea which rule they
 * hit. See issues #201 and #164.
 */
function describePeriodConflict(error: any): Error | null {
  if (!error || error.code !== '23505') return null;
  const detail = `${error.message || ''} ${error.details || ''}`;

  if (detail.includes('uq_intention_periods_one_open_per_year')) {
    return new Error(
      'There is already an open intention period for this academic year. ' +
      'Close the existing one before opening another.'
    );
  }
  if (detail.includes('uq_intention_periods_tenant_year_name')) {
    return new Error(
      'An intention period with this name already exists for this academic year. ' +
      'Give it a different name.'
    );
  }
  return null;
}

export const intentionPeriodsService = {
  /**
   * Get all intention periods, optionally filtered by status
   */
  async getAllPeriods(status?: IntentionPeriodStatus): Promise<IntentionPeriod[]> {
    try {
      let query = supabase
        .from('intention_periods')
        .select('*')
        .order('academic_year', { ascending: false })
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching intention periods:', error);
        throw error;
      }

      return data.map(this.mapDbToIntentionPeriod);
    } catch (error) {
      console.error('Error in getAllPeriods:', error);
      return [];
    }
  },

  /**
   * Get a single intention period by ID
   */
  async getPeriodById(id: string): Promise<IntentionPeriod | null> {
    try {
      const { data, error } = await supabase
        .from('intention_periods')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching intention period:', error);
        throw error;
      }

      return this.mapDbToIntentionPeriod(data);
    } catch (error) {
      console.error('Error in getPeriodById:', error);
      return null;
    }
  },

  /**
   * Get the currently open period (if any)
   */
  async getCurrentOpenPeriod(): Promise<IntentionPeriod | null> {
    try {
      const now = new Date().toISOString();

      const { data, error } = await supabase
        .from('intention_periods')
        .select('*')
        .eq('status', 'open')
        .lte('opens_at', now)
        .gte('closes_at', now)
        .order('closes_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching current open period:', error);
        throw error;
      }

      return data ? this.mapDbToIntentionPeriod(data) : null;
    } catch (error) {
      console.error('Error in getCurrentOpenPeriod:', error);
      return null;
    }
  },

  /**
   * Create a new intention period
   */
  async createPeriod(formData: IntentionPeriodFormData, createdBy?: string): Promise<IntentionPeriod> {
    try {
      // Convert local datetime strings to ISO format with timezone
      // datetime-local gives "2026-03-17T10:04" which JS interprets as local time
      const opensAtDate = new Date(formData.opensAt);
      const closesAtDate = new Date(formData.closesAt);

      const insertData: any = {
        name: formData.name,
        academic_year: formData.academicYear,
        opens_at: opensAtDate.toISOString(),
        closes_at: closesAtDate.toISOString(),
        due_date: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
        status: formData.status,
        description: formData.description || null,
        created_by: createdBy || null
      };

      const { data, error } = await supabase
        .from('intention_periods')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error creating intention period:', error);
        throw describePeriodConflict(error) || error;
      }

      return this.mapDbToIntentionPeriod(data);
    } catch (error) {
      console.error('Error in createPeriod:', error);
      throw error;
    }
  },

  /**
   * Update an existing intention period
   */
  async updatePeriod(id: string, formData: Partial<IntentionPeriodFormData>): Promise<IntentionPeriod> {
    try {
      const updateData: any = {
        updated_at: new Date().toISOString()
      };

      if (formData.name !== undefined) updateData.name = formData.name;
      if (formData.academicYear !== undefined) updateData.academic_year = formData.academicYear;
      // Convert local datetime strings to ISO format with timezone
      if (formData.opensAt !== undefined) updateData.opens_at = new Date(formData.opensAt).toISOString();
      if (formData.closesAt !== undefined) updateData.closes_at = new Date(formData.closesAt).toISOString();
      if (formData.dueDate !== undefined) updateData.due_date = formData.dueDate ? new Date(formData.dueDate).toISOString() : null;
      if (formData.status !== undefined) updateData.status = formData.status;
      if (formData.description !== undefined) updateData.description = formData.description;

      const { data, error } = await supabase
        .from('intention_periods')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating intention period:', error);
        // Reopening a period can collide with an already-open one for that year.
        throw describePeriodConflict(error) || error;
      }

      return this.mapDbToIntentionPeriod(data);
    } catch (error) {
      console.error('Error in updatePeriod:', error);
      throw error;
    }
  },

  /**
   * Delete an intention period (only if no submissions exist)
   */
  async deletePeriod(id: string): Promise<void> {
    try {
      // Check if there are any submissions for this period
      const { count, error: countError } = await supabase
        .from('teaching_intentions')
        .select('*', { count: 'exact', head: true })
        .eq('period_id', id);

      if (countError) {
        console.error('Error checking submissions:', countError);
        throw countError;
      }

      if (count && count > 0) {
        throw new Error(`Cannot delete period: ${count} submission(s) exist. Archive the period instead.`);
      }

      const { error } = await supabase
        .from('intention_periods')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting intention period:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deletePeriod:', error);
      throw error;
    }
  },

  /**
   * Open a period (set status to 'open')
   */
  async openPeriod(id: string): Promise<IntentionPeriod> {
    return this.updatePeriod(id, { status: 'open' });
  },

  /**
   * Close a period (set status to 'closed')
   */
  async closePeriod(id: string): Promise<IntentionPeriod> {
    return this.updatePeriod(id, { status: 'closed' });
  },

  /**
   * Archive a period
   */
  async archivePeriod(id: string): Promise<IntentionPeriod> {
    return this.updatePeriod(id, { status: 'archived' });
  },

  /**
   * Get submission statistics for a period
   */
  async getPeriodStats(periodId: string): Promise<IntentionPeriodStats | null> {
    try {
      // Active staff roster with the fields the shared stat helper needs. The
      // denominator is teaching staff only (see utils/intentionStats), so the
      // rate matches /intentions, the dashboard and Reports exactly.
      const { data: staffRows, error: staffError } = await supabase
        .from('staff')
        .select('role, status, staff_code, initial, staff_id')
        .eq('status', 'active');

      if (staffError) {
        console.error('Error fetching staff:', staffError);
        throw staffError;
      }

      // Submissions for this period (staff code + status).
      const { data: submissions, error: submissionError } = await supabase
        .from('teaching_intentions')
        .select('staff_id, status')
        .eq('period_id', periodId);

      if (submissionError) {
        console.error('Error fetching submissions:', submissionError);
        throw submissionError;
      }

      const stats = computeIntentionResponseStats(
        (submissions || []).map((s: any) => ({ staffId: s.staff_id, status: s.status })),
        staffRows || [],
        'teaching'
      );

      return {
        periodId,
        totalStaff: stats.expectedCount,
        totalSubmissions: stats.submittedCount,
        draftCount: stats.byStatus['draft'] || 0,
        submittedCount: stats.byStatus['submitted'] || 0,
        hrApprovedCount: stats.byStatus['hr_approved'] || 0,
        hrFlaggedCount: stats.byStatus['hr_flagged'] || 0,
        pendingReviewCount: stats.byStatus['submitted'] || 0,
        submissionRate: stats.responseRate
      };
    } catch (error) {
      console.error('Error in getPeriodStats:', error);
      return null;
    }
  },

  /**
   * Get periods by academic year
   */
  async getPeriodsByYear(year: number): Promise<IntentionPeriod[]> {
    try {
      const { data, error } = await supabase
        .from('intention_periods')
        .select('*')
        .eq('academic_year', year)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching periods by year:', error);
        throw error;
      }

      return data.map(this.mapDbToIntentionPeriod);
    } catch (error) {
      console.error('Error in getPeriodsByYear:', error);
      return [];
    }
  },

  /**
   * Check if a period is currently accepting submissions
   */
  isPeriodOpen(period: IntentionPeriod): boolean {
    if (period.status !== 'open') return false;

    const now = new Date();
    return now >= period.opensAt && now <= period.closesAt;
  },

  /**
   * Get time remaining until period closes
   */
  getTimeUntilClose(period: IntentionPeriod): { days: number; hours: number; minutes: number } | null {
    if (!this.isPeriodOpen(period)) return null;

    const now = new Date();
    const closeTime = period.closesAt;
    const diff = closeTime.getTime() - now.getTime();

    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return { days, hours, minutes };
  },

  /**
   * Map database row to IntentionPeriod type
   */
  mapDbToIntentionPeriod(row: any): IntentionPeriod {
    return {
      id: row.id,
      name: row.name,
      academicYear: row.academic_year,
      opensAt: new Date(row.opens_at),
      dueDate: row.due_date ? new Date(row.due_date) : undefined,
      closesAt: new Date(row.closes_at),
      status: row.status,
      description: row.description,
      createdBy: row.created_by,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at)
    };
  }
};
